import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { useCreateConversation } from '@/hooks/useMessages';
import type { ConversationType } from '@/types/messaging';
import { API_URL } from '@/services/api';

import Avatar from '../common/Avatar';
import Button, { IconButton } from '../common/Button';
import Modal from '../common/Modal';
import FaIcon from '../common/FaIcon';
import Input from '../common/Input';

// ESF (Emergency Support Function) Templates
const ESF_TEMPLATES = [
  { id: 'esf-1', name: 'ESF 1 - Transportation', description: 'Transportation infrastructure and services' },
  { id: 'esf-2', name: 'ESF 2 - Communications', description: 'Communications infrastructure and support' },
  { id: 'esf-3', name: 'ESF 3 - Public Works & Engineering', description: 'Infrastructure protection and restoration' },
  { id: 'esf-4', name: 'ESF 4 - Firefighting', description: 'Firefighting activities and support' },
  { id: 'esf-5', name: 'ESF 5 - Information & Planning', description: 'Information collection and planning' },
  { id: 'esf-6', name: 'ESF 6 - Mass Care', description: 'Mass care, housing, and human services' },
  { id: 'esf-7', name: 'ESF 7 - Logistics', description: 'Resource support and logistics' },
  { id: 'esf-8', name: 'ESF 8 - Public Health & Medical', description: 'Public health and medical services' },
  { id: 'esf-9', name: 'ESF 9 - Search & Rescue', description: 'Urban search and rescue operations' },
  { id: 'esf-10', name: 'ESF 10 - Hazardous Materials', description: 'Oil and hazardous materials response' },
  { id: 'esf-11', name: 'ESF 11 - Agriculture & Natural Resources', description: 'Agriculture and natural resources protection' },
  { id: 'esf-12', name: 'ESF 12 - Energy', description: 'Energy infrastructure and restoration' },
  { id: 'esf-13', name: 'ESF 13 - Public Safety & Security', description: 'Public safety and security support' },
  { id: 'esf-14', name: 'ESF 14 - Cross-Sector Business', description: 'Cross-sector business and infrastructure' },
  { id: 'esf-15', name: 'ESF 15 - External Affairs', description: 'External affairs and public information' },
];

// ICS (Incident Command System) Section Templates
const ICS_TEMPLATES = [
  { id: 'ics-command', name: 'Command Staff', description: 'Incident Commander, PIO, Safety Officer, Liaison' },
  { id: 'ics-operations', name: 'Operations Section', description: 'Tactical operations and field activities' },
  { id: 'ics-planning', name: 'Planning Section', description: 'Situation analysis and planning' },
  { id: 'ics-logistics', name: 'Logistics Section', description: 'Resources, supplies, and facilities' },
  { id: 'ics-finance', name: 'Finance/Admin Section', description: 'Cost tracking and administrative support' },
];

interface NewConversationModalProps {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}

interface User {
  user_id: string;
  name: string;
  email: string;
}

type CreationMode = 'direct' | 'group' | 'template';

export function NewConversationModal({ onClose, onCreated }: NewConversationModalProps) {
  const [creationMode, setCreationMode] = useState<CreationMode>('direct');
  const [conversationType, setConversationType] = useState<ConversationType>('direct');
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'esf' | 'ics'>('esf');

  const { token, user } = useAuthStore();
  const { activeTenant } = useTenantStore();
  const createConversation = useCreateConversation();

  // Fetch users for selection
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-for-messaging', activeTenant?.id, searchQuery],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/tenants/${activeTenant?.id}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': activeTenant?.id || '',
        },
        params: { search: searchQuery },
      });
      return response.data as User[];
    },
    enabled: !!activeTenant?.id,
  });

  const users = (usersData || []).filter((u) => u.user_id !== user?.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedUsers.length === 0) return;

    try {
      const result = await createConversation.mutateAsync({
        type: conversationType,
        name: conversationType === 'group' ? groupName : undefined,
        description: conversationType === 'group' ? groupDescription : undefined,
        participant_ids: selectedUsers,
      });

      onCreated(result.id);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleTemplateSelect = async (template: { id: string; name: string; description: string }) => {
    try {
      const result = await createConversation.mutateAsync({
        type: 'group',
        name: template.name,
        description: template.description,
        participant_ids: [], // Creator will be added automatically
      });

      onCreated(result.id);
    } catch (error) {
      console.error('Failed to create group from template:', error);
    }
  };

  const toggleUser = (userId: string) => {
    if (conversationType === 'direct') {
      setSelectedUsers([userId]);
    } else {
      setSelectedUsers((prev) =>
        prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
      );
    }
  };

  // Reset selection when mode/type changes
  useEffect(() => {
    setSelectedUsers([]);
    if (creationMode === 'direct') {
      setConversationType('direct');
    } else if (creationMode === 'group' || creationMode === 'template') {
      setConversationType('group');
    }
  }, [creationMode]);

  useEffect(() => {
    setSelectedUsers([]);
  }, [conversationType]);

  return (
    <Modal isOpen onClose={onClose} title="New Conversation" size="md">

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Mode selector */}
          <div className="px-4 py-3 border-b border-theme-primary">
            <div className="flex gap-2">
              <Button
                variant={creationMode === 'direct' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setCreationMode('direct')}
                className="flex-1"
              >
                Direct
              </Button>
              <Button
                variant={creationMode === 'group' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setCreationMode('group')}
                className="flex-1"
              >
                Group
              </Button>
              <Button
                variant={creationMode === 'template' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setCreationMode('template')}
                className="flex-1"
              >
                Template
              </Button>
            </div>

            {creationMode === 'group' && (
              <div className="mt-3 space-y-2">
                <Input aria-label="Group name"
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name"
                />
                <Input aria-label="Description (optional)"
                  type="text"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Description (optional)"
                />
              </div>
            )}

            {creationMode === 'template' && (
              <div className="mt-3 flex gap-2">
                <Button
                  variant={templateCategory === 'esf' ? 'warning' : 'secondary'}
                  size="sm"
                  onClick={() => setTemplateCategory('esf')}
                  className="flex-1 text-xs"
                >
                  ESF Groups
                </Button>
                <Button
                  variant={templateCategory === 'ics' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setTemplateCategory('ics')}
                  className="flex-1 text-xs"
                >
                  ICS Sections
                </Button>
              </div>
            )}
          </div>

          {/* Template mode content */}
          {creationMode === 'template' ? (
            <div className="flex-1 overflow-y-auto px-4 py-2">
              <div className="space-y-2">
                {(templateCategory === 'esf' ? ESF_TEMPLATES : ICS_TEMPLATES).map((template) => (
                  <Button
                    key={template.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleTemplateSelect(template)}
                    disabled={createConversation.isPending}
                    className="w-full flex items-start gap-3 px-3 py-3 text-left justify-start"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                      templateCategory === 'esf' ? 'bg-orange-500' : 'bg-primary-500'
                    }`}>
                      {templateCategory === 'esf'
                        ? template.id.replace('esf-', '')
                        : template.id.replace('ics-', '').charAt(0).toUpperCase()
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-theme-primary">
                        {template.name}
                      </p>
                      <p className="text-xs text-theme-tertiary mt-0.5">
                        {template.description}
                      </p>
                    </div>
                    <FaIcon icon="plus" className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  </Button>
                ))}
              </div>
              <p className="text-xs text-theme-tertiary text-center mt-4">
                Click a template to create a group. You can add members after creation.
              </p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="px-4 py-2">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  aria-label="Search users"
                  autoFocus
                />
              </div>

              {/* Selected users (for group) */}
              {creationMode === 'group' && selectedUsers.length > 0 && (
                <div className="px-4 py-2 flex flex-wrap gap-2">
                  {selectedUsers.map((userId) => {
                    const selectedUser = users.find((u) => u.user_id === userId);
                    if (!selectedUser) return null;
                    return (
                      <span
                        key={userId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-sm"
                      >
                        {selectedUser.name}
                        <IconButton
                          icon={<FaIcon icon="xmark" className="w-3 h-3" />}
                          label="Remove user"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleUser(userId)}
                          className="p-0 min-h-0 min-w-0 hover:text-primary-900 dark:hover:text-primary-100"
                        />
                      </span>
                    );
                  })}
                </div>
              )}

              {/* User list */}
              <div className="flex-1 overflow-y-auto px-4 py-2">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-center text-theme-tertiary py-8 text-sm">
                    No users found
                  </p>
                ) : (
                  <div className="space-y-1">
                    {users.map((u) => (
                      <Button
                        key={u.user_id}
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUser(u.user_id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 justify-start ${
                          selectedUsers.includes(u.user_id)
                            ? 'bg-primary-50 dark:bg-gray-700'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Avatar src={undefined} name={u.name} size="sm" />
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-sm font-medium text-theme-primary truncate">
                            {u.name}
                          </p>
                          <p className="text-xs text-theme-tertiary truncate">
                            {u.email}
                          </p>
                        </div>
                        {selectedUsers.includes(u.user_id) && (
                          <FaIcon icon="check" className="w-5 h-5 text-primary-500" />
                        )}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          {creationMode !== 'template' && (
            <div className="px-4 py-3 border-t border-theme-primary flex gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={selectedUsers.length === 0 || createConversation.isPending}
                loading={createConversation.isPending}
                className="flex-1"
              >
                {createConversation.isPending ? 'Creating...' : 'Start Chat'}
              </Button>
            </div>
          )}
      </form>
    </Modal>
  );
}
